<template>
  <JoszakiModal
    :open="open"
    :title="$t('conductor.dataTable.export.title')"
    size="md"
    :loading="isExporting"
    :disable-confirm="isExporting"
    :confirm-button-label="$t('conductor.dataTable.export.confirm')"
    @confirm="performExport"
    @cancel="handleClose"
    @close="handleClose"
  >
    <template #body>
      <div class="space-y-2">
        <JoszakiRadio
          v-model="exportOption"
          :options="exportOptions"
          :disabled="isExporting"
        />

        <JoszakiCard
          v-if="isLargeDataSet && exportOption === 'all'"
          type="warning"
        >
          {{
            $t("conductor.dataTable.export.largeDataSetWarning", {
              limit: SOFT_LIMIT,
            })
          }}
        </JoszakiCard>

        <JoszakiCard v-if="isOverLimit" type="warning">
          {{
            $t("conductor.dataTable.export.limitExceeded", {
              limit: HARD_LIMIT,
            })
          }}
        </JoszakiCard>

        <div
          v-if="isExporting"
          class="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg"
        >
          <IconComponent icon="spinner" class="animate-spin text-blue-600" />
          <span class="text-sm text-gray-700">{{ currentExportProgress }}</span>
        </div>
      </div>
    </template>
  </JoszakiModal>
</template>

<script>
import { ref, computed, useContext, watch } from "@nuxtjs/composition-api";
import get from "lodash/get";

const HARD_LIMIT = 50000;
const SOFT_LIMIT = 10000;

export default {
  name: "JoszakiExportModal",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    totalItems: {
      type: Number,
      required: true,
    },
    currentPageItems: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    displayedColumns: {
      type: Array,
      required: true,
    },
    loadFunction: {
      type: Function,
      required: true,
    },
    fields: {
      type: Array,
      default: undefined,
    },
    currentFilters: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { i18n, $logger, $joszaki } = useContext();
    const exportOption = ref("all");
    const isExporting = ref(false);
    const currentExportProgress = ref("");

    const isOverLimit = computed(() => props.totalItems > HARD_LIMIT);

    const isLargeDataSet = computed(() => props.totalItems > SOFT_LIMIT);

    const exportOptions = computed(() => [
      {
        value: "current",
        label: `${i18n.t("conductor.dataTable.export.currentPage")} (${
          props.currentPageItems.length
        } ${i18n.t("conductor.dataTable.export.items")})`,
      },
      {
        value: "all",
        label: `${i18n.t("conductor.dataTable.export.allPages")} (${
          props.totalItems
        } ${i18n.t("conductor.dataTable.export.items")})`,
        disabled: isOverLimit.value,
      },
    ]);

    const handleClose = () => {
      if (!isExporting.value) {
        emit("close");
      }
    };

    const stripIdHash = (value) =>
      typeof value === "string" ? value.replace(/^#(\d+)$/, "$1") : value;

    const valueFormatter = (column, row) => {
      if (column.formatter) {
        return JSON.stringify(stripIdHash(column.formatter(row)));
      }
      if (typeof get(row, column.name) === "boolean") {
        return JSON.stringify(get(row, column.name) ? "✓" : "✗");
      }
      return JSON.stringify(get(row, column.name, "-"));
    };

    const generateCSV = (items) => {
      const actualColumns = props.columns.filter((col) =>
        props.displayedColumns.includes(col.name)
      );
      const header = actualColumns
        .map((c) => JSON.stringify(c.label))
        .join(",");
      const csv = [
        header,
        ...items.map((row) =>
          actualColumns.map((column) => valueFormatter(column, row)).join(",")
        ),
      ].join("\r\n");

      return csv;
    };

    const downloadCSV = (csvContent) => {
      const textBlob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(textBlob);

      // Generate filename with timestamp
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/T/, "-")
        .replace(/\..+/, "")
        .replace(/:/g, "-");
      const filename = `export-${timestamp}.csv`;

      // Create download link and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the URL
      URL.revokeObjectURL(url);
    };

    const exportCurrentPage = () => {
      const csv = generateCSV(props.currentPageItems);
      downloadCSV(csv);
    };

    const fetchAllPages = async () => {
      const allItems = [];

      // Fetch first page with pageSize=100 to get correct pagination info
      currentExportProgress.value = i18n.t(
        "conductor.dataTable.export.fetching",
        {
          items: 0,
          total: props.totalItems,
        }
      );

      try {
        const firstPageResponse = await props.loadFunction(
          {
            page: 1,
            pageSize: 100,
            search: props.currentFilters.search,
            orderBy: props.currentFilters.orderBy,
            order: props.currentFilters.order,
            filters: props.currentFilters.filters,
          },
          { fields: props.fields }
        );

        allItems.push(...firstPageResponse.items);

        // Get the correct number of pages based on pageSize=100
        const totalPages = firstPageResponse.pagination.last;

        // Fetch remaining pages if there are any
        for (let page = 2; page <= totalPages; page++) {
          currentExportProgress.value = i18n.t(
            "conductor.dataTable.export.fetching",
            {
              items: allItems.length,
              total: props.totalItems,
            }
          );

          const response = await props.loadFunction(
            {
              page,
              pageSize: 100,
              search: props.currentFilters.search,
              orderBy: props.currentFilters.orderBy,
              order: props.currentFilters.order,
              filters: props.currentFilters.filters,
            },
            { fields: props.fields }
          );

          allItems.push(...response.items);
        }

        return allItems;
      } catch (error) {
        $logger.error("Error fetching pages during export", error);
        throw error;
      }
    };

    const exportAllPages = async () => {
      try {
        const allItems = await fetchAllPages();
        const csv = generateCSV(allItems);
        downloadCSV(csv);
      } catch (error) {
        $joszaki.toast({
          message: i18n.t("conductor.dataTable.export.error"),
          type: "error",
        });
        throw error;
      }
    };

    const performExport = async () => {
      isExporting.value = true;

      try {
        if (exportOption.value === "current") {
          exportCurrentPage();
        } else {
          await exportAllPages();
        }

        emit("close");
      } catch (error) {
        $logger.error("Error during CSV export", error);
      } finally {
        isExporting.value = false;
        currentExportProgress.value = "";
      }
    };

    // Reset to current page if all pages is selected but becomes disabled
    watch(isOverLimit, (newValue) => {
      if (newValue && exportOption.value === "all") {
        exportOption.value = "current";
      }
    });

    return {
      HARD_LIMIT,
      SOFT_LIMIT,
      exportOption,
      isExporting,
      currentExportProgress,
      exportOptions,
      handleClose,
      performExport,
      isOverLimit,
      isLargeDataSet,
    };
  },
};
</script>
